/**
 * Cadre de vie d'un hébergement (story cadre-de-vie, 4.141.0) : deux axes,
 * une valeur par axe. Miroir des enums Prisma `LivingLocality`,
 * `LivingSurroundings` et `LivingSettingProvenance` — partagé entre le
 * serveur (validation) et l'espace propriétaire (listes de choix).
 */
export const LIVING_LOCALITIES = ['CITY', 'VILLAGE', 'COUNTRYSIDE'] as const
export type LivingLocalityValue = typeof LIVING_LOCALITIES[number]

export const LIVING_SURROUNDINGS = ['SEASIDE', 'MOUNTAIN', 'NONE'] as const
export type LivingSurroundingsValue = typeof LIVING_SURROUNDINGS[number]

export const LIVING_SETTING_PROVENANCES = ['AUTO', 'OWNER'] as const
export type LivingSettingProvenanceValue = typeof LIVING_SETTING_PROVENANCES[number]

export function isLivingLocality(value: unknown): value is LivingLocalityValue {
  return typeof value === 'string' && (LIVING_LOCALITIES as readonly string[]).includes(value)
}

export function isLivingSurroundings(value: unknown): value is LivingSurroundingsValue {
  return typeof value === 'string' && (LIVING_SURROUNDINGS as readonly string[]).includes(value)
}

/**
 * Filtre cadre de vie (story filtre-cadre-de-vie-au-panneau-filtres, 4.144.0) :
 * découpe une valeur d'URL `livingLocality` / `livingSurroundings` (chaîne à
 * virgules ou tableau de chaînes) en liste triée et dédoublonnée, entrées vides
 * écartées. Les valeurs inconnues sont conservées : le schéma serveur les
 * rejette (400). Même contrat que `splitSejoursParam`.
 */
export function splitLivingSettingParam(value: unknown): string[] {
  const raw = Array.isArray(value) ? value : [value]
  const parts = raw
    .filter((v): v is string => typeof v === 'string')
    .flatMap(v => v.split(','))
    .map(v => v.trim())
    .filter(v => v.length > 0)
  return [...new Set(parts)].sort()
}

/** Lecture tolérante côté client : seules les localités connues sont gardées. */
export function parseLivingLocalityParam(value: unknown): LivingLocalityValue[] {
  return splitLivingSettingParam(value).filter(isLivingLocality)
}

/** Lecture tolérante côté client : seuls les milieux connus sont gardés. */
export function parseLivingSurroundingsParam(value: unknown): LivingSurroundingsValue[] {
  return splitLivingSettingParam(value).filter(isLivingSurroundings)
}

/** Valeur d'URL `livingLocality` (triée, jointe par des virgules) ou `undefined` si vide. */
export function formatLivingLocalityParam(value: unknown): string | undefined {
  return parseLivingLocalityParam(value).join(',') || undefined
}

/** Valeur d'URL `livingSurroundings` (triée, jointe par des virgules) ou `undefined` si vide. */
export function formatLivingSurroundingsParam(value: unknown): string | undefined {
  return parseLivingSurroundingsParam(value).join(',') || undefined
}

/**
 * Filtre cadre de vie (4.144.0) tenu sur place par les pages département et
 * région, comme `sejours` : deux listes validées, triées, dédoublonnées.
 */
export interface LivingSettingFilter {
  livingLocality: LivingLocalityValue[]
  livingSurroundings: LivingSurroundingsValue[]
}

export const LIVING_SETTING_QUERY_KEYS = ['livingLocality', 'livingSurroundings'] as const

type LivingSettingLike = { livingLocality?: unknown, livingSurroundings?: unknown }

/** Lecture tolérante (URL ou `SearchFilters`) : valeurs hors enum ignorées. */
export function parseLivingSettingQuery(query: LivingSettingLike): LivingSettingFilter {
  return {
    livingLocality: parseLivingLocalityParam(query.livingLocality),
    livingSurroundings: parseLivingSurroundingsParam(query.livingSurroundings),
  }
}

/** Paramètres d'URL / d'API du cadre de vie, clés vides omises. */
export function livingSettingQuery(living: LivingSettingLike | undefined): Record<string, string> {
  const query: Record<string, string> = {}
  if (!living) return query
  const locality = formatLivingLocalityParam(living.livingLocality)
  if (locality) query.livingLocality = locality
  const surroundings = formatLivingSurroundingsParam(living.livingSurroundings)
  if (surroundings) query.livingSurroundings = surroundings
  return query
}

/** Listes validées du cadre de vie (modèle `SearchFilters`), axes vides omis. */
export function livingSettingFilters(value: LivingSettingLike | undefined): Partial<LivingSettingFilter> {
  const filters: Partial<LivingSettingFilter> = {}
  if (!value) return filters
  const { livingLocality, livingSurroundings } = parseLivingSettingQuery(value)
  if (livingLocality.length > 0) filters.livingLocality = livingLocality
  if (livingSurroundings.length > 0) filters.livingSurroundings = livingSurroundings
  return filters
}

/** Vrai si au moins un axe porte une valeur valide. */
export function hasLivingSetting(value: LivingSettingLike | undefined): boolean {
  return Object.keys(livingSettingFilters(value)).length > 0
}

/** Égalité de deux filtres cadre de vie (ordre indifférent, valeurs validées). */
export function sameLivingSetting(a: Partial<LivingSettingFilter> | undefined, b: Partial<LivingSettingFilter> | undefined): boolean {
  const qa = livingSettingQuery(a)
  const qb = livingSettingQuery(b)
  return qa.livingLocality === qb.livingLocality && qa.livingSurroundings === qb.livingSurroundings
}

/** Valeurs d'un hébergement pour un corps de PUT propriétaire : axes `null` omis. */
export function livingSettingValues(value: {
  livingLocality: LivingLocalityValue | null
  livingSurroundings: LivingSurroundingsValue | null
}): { livingLocality?: LivingLocalityValue, livingSurroundings?: LivingSurroundingsValue } {
  return {
    ...(value.livingLocality ? { livingLocality: value.livingLocality } : {}),
    ...(value.livingSurroundings ? { livingSurroundings: value.livingSurroundings } : {}),
  }
}
